<script setup lang="ts">
import {NotificationSequenceDetails} from "@/notification-sequences/interfaces/NotificationSequence";
import {computed, ref} from "vue";
import {notificationSequenceApi} from "@/container";
import NotificationSequencePreview
    from "@/notification-sequences/components/notification-sequence-preview/NotificationSequencePreview.vue";
import {Send} from "@lucide/vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import formatHelper from "@/core/helpers/formatHelper";
import {useToastStore} from "@/stores/toast.store";
import {Dialog, DialogTrigger} from "@/core/components/ui/dialog";
import PreCertificationDialogContent
    from "@/customer-certification/components/pre-certification-dialog/PreCertificationDialogContent.vue";
import {isAxiosError} from "axios";

const props = defineProps<{
    sequence: NotificationSequenceDetails;
    customer: Customer;
    consumerKey: string;
    /** The beneficiary the sequence is started for, on behalf of the insured customer. */
    beneficiary?: Beneficiary;
}>();

const emits = defineEmits<{
    (e: 'started'): void;
    (e: 'certified'): void
}>();

const {showSuccess, showError} = useToastStore();

const isStarting = ref<boolean>(false);

/** Estimated start date of the sequence, assuming it is started today. */
const startDate = new Date();

const uncertified = computed(() => {
    return props.customer?.status === 'none';
});

const messageCountLabel = computed(() => {
    const count = props.sequence.messages.length;

    return `${count} message${count > 1 ? 's' : ''} programmé${count > 1 ? 's' : ''}`;
});

const startSequence = async () => {
    try {
        await notificationSequenceApi().createInstance(props.sequence.id, {
            consumer_key: props.consumerKey
        });

        showSuccess({
            content: 'La séquence a été démarrée avec succès'
        });
        emits('started');
    } catch (e) {
        if (isAxiosError(e) && e.response?.status === 409) {
            showError({
                content: 'La séquence a déjà été démarrée pour ce patient'
            });
        } else {
            showError({
                content: 'Une erreur est survenue lors du démarrage de la séquence'
            });
        }
    }
};
</script>

<template>
  <div class="flex flex-col gap-4 min-h-0">
    <NotificationSequencePreview class="flex-1 min-h-0" :sequence="sequence" :start-date="startDate">
      <template #before-messages>
        <div>
          <h2>Programme d'envoi de la séquence</h2>
          <p class="text-sm text-muted-foreground">
            {{ sequence.messages?.length > 1
              ? `${sequence.messages.length} messages envoyés automatiquement après le démarrage.`
              : `Le message suivant sera envoyé automatiquement au patient après le démarrage.` }}
          </p>
          <p v-if="beneficiary && customer.information?.phone" class="text-sm text-muted-foreground">
            Les messages seront envoyés à {{ customer.information.first_name }} {{ customer.information.last_name }}
            (assuré) au {{ formatHelper.readablePhone(customer.information.phone) }}.
          </p>
        </div>
      </template>

      <div>
        <Dialog v-if="uncertified && customer">
          <DialogTrigger as-child>
            <BaseButton primary density="comfortable" class="shrink-0 w-full">
              Démarrer la séquence
              <Send class="ml-2 w-4" />
            </BaseButton>
          </DialogTrigger>
          <PreCertificationDialogContent :customer="customer" :on-certified="() => $emit('certified')" />
        </Dialog>
        <BaseButton
          v-else-if="customer?.information?.phone"
          primary
          density="comfortable"
          class="shrink-0 w-full"
          :loading="isStarting"
          @click="startSequence"
        >
          Démarrer la séquence
          <Send class="ml-2 w-4" />
        </BaseButton>
        <p v-else class="text-sm text-muted-foreground shrink-0">
          Numéro de téléphone requis
        </p>
      </div>
    </NotificationSequencePreview>

    <div class="flex-shrink-0 border-t pt-4 flex items-center justify-between gap-4">
      <div class="text-sm">
        <p class="font-medium">
          Séquence complète
        </p>
        <p class="text-muted-foreground">
          {{ messageCountLabel }}
          <template v-if="customer?.information?.phone">
            · {{ formatHelper.readablePhone(customer.information.phone) }}
          </template>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.rcs-view) {
    @apply max-w-xs lg:max-w-sm
}

:deep(.sequence-preview-right) {
    @apply self-start pt-10
}
</style>
